import type { Customer, Item, ItemStatus, ItemUsage, Part, Site } from '../../domain/inventory';
import type { PenActivity } from '../../domain/notification';
import { statusLabels } from '../../domain/status';
import { siteLabel } from '../../lib/site-label';
import { daysSince, GRACE_DAYS, isDualDeployment } from './active-breakdown';

export const deviceCategories = [
  { key: 'gen1-camera', label: 'Gen1 camera', shortLabel: 'Gen1 cam' },
  { key: 'gen2-camera', label: 'Gen2 camera', shortLabel: 'Gen2 cam' },
  { key: 'gen1-winch', label: 'Gen1 winch', shortLabel: 'Gen1 winch' },
  { key: 'gen2-winch', label: 'Gen2 winch', shortLabel: 'Gen2 winch' },
  { key: 'unit-controller', label: 'Unit controller', shortLabel: 'Unit ctrl' },
  { key: 'powerbox', label: 'Powerbox', shortLabel: 'Powerbox' },
  { key: 'site-controller', label: 'Site controller', shortLabel: 'Site ctrl' },
] as const;

export type DeviceCategory = (typeof deviceCategories)[number]['key'];
export type DeviceCounts = Record<DeviceCategory, number>;

const systemGroups: {
  key: string;
  label: string;
  statuses: ItemStatus[];
}[] = [
  {
    key: 'workshop',
    label: 'Workshop',
    statuses: ['CLEANING', 'EOL_TESTING', 'MAINTENANCE_IN_PROGRESS'],
  },
  {
    key: 'warehouse',
    label: 'Warehouse',
    statuses: ['DELIVERED_TO_WAREHOUSE', 'IN_WAREHOUSE_STORAGE', 'READY_TO_SHIP'],
  },
  {
    key: 'customer',
    label: 'Customer',
    statuses: [
      'ACTIVE',
      'DELIVERED_TO_CUSTOMER',
      'REPLACED',
      'RETURN_STARTED',
      'SHIPPED_TO_CUSTOMER',
    ],
  },
  {
    key: 'failure-analysis',
    label: 'Failure analysis',
    statuses: ['NEEDS_REPAIR', 'RMA_IN_PROGRESS'],
  },
  { key: 'scrap', label: 'Scrap', statuses: ['SCRAP'] },
  { key: 'unresolved', label: 'Unresolved status', statuses: ['STATUS_UNSPECIFIED'] },
];

function emptyCounts(): DeviceCounts {
  return {
    'gen1-camera': 0,
    'gen2-camera': 0,
    'gen1-winch': 0,
    'gen2-winch': 0,
    'unit-controller': 0,
    powerbox: 0,
    'site-controller': 0,
  };
}

function increment(counts: DeviceCounts, category: DeviceCategory): void {
  counts[category] += 1;
}

function totalCounts(counts: DeviceCounts): number {
  return deviceCategories.reduce((sum, category) => sum + counts[category.key], 0);
}

function addCounts(target: DeviceCounts, source: DeviceCounts): void {
  for (const category of deviceCategories) target[category.key] += source[category.key];
}

// Early engineering builds (EVT, DVT_1-3, ...) aren't a real hardware
// generation in the field -- excluding them (rather than lumping them into
// Gen1) keeps the summary matching the Grafana dashboard's fleet counts.
// See https://github.com/tidaltech/koi/issues/7796.
const GEN1_BUILDS = new Set(['DVT_4', 'PVT', 'MP']);
const GEN2_BUILDS = new Set(['VVDN']);

export function deviceCategoryForPart(part: Part | undefined): DeviceCategory | null {
  if (!part) return null;
  if (part.type === 'CAMERA_SYSTEM') {
    if (GEN2_BUILDS.has(part.build)) return 'gen2-camera';
    if (GEN1_BUILDS.has(part.build)) return 'gen1-camera';
    return null;
  }
  if (part.type === 'WINCH_SYSTEM') {
    if (GEN2_BUILDS.has(part.build)) return 'gen2-winch';
    if (GEN1_BUILDS.has(part.build)) return 'gen1-winch';
    return null;
  }
  if (part.type === 'UNIT_CONTROLLER') return 'unit-controller';
  if (part.type === 'POWERBOX') return 'powerbox';
  if (part.type === 'SITE_CONTROLLER') return 'site-controller';
  return null;
}

export interface SiteAllocationRow<C = DeviceCounts> {
  key: string;
  label: string;
  counts: C;
  total: number;
  siteId: string;
}

export interface RegionAllocationRow<C = DeviceCounts> {
  key: string;
  label: string;
  counts: C;
  total: number;
  siteIds: string[];
  sites: SiteAllocationRow<C>[];
}

export interface CountryAllocationRow<C = DeviceCounts> {
  key: string;
  label: string;
  counts: C;
  total: number;
  siteIds: string[];
  regions: RegionAllocationRow<C>[];
}

export interface CustomerAllocationRow<C = DeviceCounts> {
  customerId: string;
  customerName: string;
  counts: C;
  total: number;
  countries: CountryAllocationRow<C>[];
}

export interface CustomerAllocation<C = DeviceCounts> {
  rows: CustomerAllocationRow<C>[];
  totals: C;
  total: number;
}

function geographyForSite(site: Site): {
  countryKey: string;
  countryLabel: string;
  regionKey: string;
  regionLabel: string;
} {
  const country = site.labels?.country?.trim() || '';
  const region = site.labels?.region?.trim() || site.region?.trim() || '';
  return {
    countryKey: country || 'unrecorded-country',
    countryLabel: country || 'Not recorded',
    regionKey: `${country || 'unrecorded-country'}:${region || 'unrecorded-region'}`,
    regionLabel: region || 'Not recorded',
  };
}

function isExcludedCustomerName(name: string): boolean {
  return ['unknown', 'test customer'].includes(name.trim().toLowerCase());
}

/**
 * One thing to count at a site: `add` adds it to a counts record, and
 * `items` is how many items it is (0 for a per-site number such as active
 * pens), so columns that overlap don't inflate a row's total.
 */
interface AllocationEntry<C> {
  siteId: string;
  add: (counts: C) => void;
  items: number;
}

/** Rolls entries up customer -> country -> region -> site. */
function buildCustomerAllocation<C extends Record<string, number>>(
  entries: Iterable<AllocationEntry<C>>,
  sitesById: ReadonlyMap<string, Site>,
  customersById: ReadonlyMap<string, Customer>,
  empty: () => C,
  includeCustomer: (customer: Customer) => boolean = () => true,
): CustomerAllocation<C> {
  const byCustomer = new Map<string, CustomerAllocationRow<C>>();
  const countriesByCustomer = new Map<string, Map<string, CountryAllocationRow<C>>>();
  const regionsByCustomer = new Map<string, Map<string, RegionAllocationRow<C>>>();
  const sitesByCustomer = new Map<string, Map<string, SiteAllocationRow<C>>>();
  const totals = empty();
  let total = 0;

  for (const entry of entries) {
    const site = sitesById.get(entry.siteId);
    if (!site) continue;
    const customer = customersById.get(site.customerId);
    if (!customer || !includeCustomer(customer)) continue;

    const customerName = customer.displayName ?? customer.name;
    if (isExcludedCustomerName(customerName)) continue;

    const geography = geographyForSite(site);
    const existing = byCustomer.get(customer.id) ?? {
      customerId: customer.id,
      customerName,
      counts: empty(),
      total: 0,
      countries: [],
    };
    const customerCountries =
      countriesByCustomer.get(customer.id) ?? new Map<string, CountryAllocationRow<C>>();
    const country = customerCountries.get(geography.countryKey) ?? {
      key: geography.countryKey,
      label: geography.countryLabel,
      counts: empty(),
      total: 0,
      siteIds: [],
      regions: [],
    };
    const customerRegions =
      regionsByCustomer.get(customer.id) ?? new Map<string, RegionAllocationRow<C>>();
    const region = customerRegions.get(geography.regionKey) ?? {
      key: geography.regionKey,
      label: geography.regionLabel,
      counts: empty(),
      total: 0,
      siteIds: [],
      sites: [],
    };
    const customerSites =
      sitesByCustomer.get(customer.id) ?? new Map<string, SiteAllocationRow<C>>();
    const siteRow = customerSites.get(site.id) ?? {
      key: site.id,
      label: siteLabel(site),
      counts: empty(),
      total: 0,
      siteId: site.id,
    };

    for (const counts of [siteRow.counts, region.counts, country.counts, existing.counts, totals]) {
      entry.add(counts);
    }
    siteRow.total += entry.items;
    region.total += entry.items;
    country.total += entry.items;
    existing.total += entry.items;
    total += entry.items;

    byCustomer.set(customer.id, existing);
    if (!country.siteIds.includes(site.id)) country.siteIds.push(site.id);
    customerCountries.set(geography.countryKey, country);
    countriesByCustomer.set(customer.id, customerCountries);
    if (!region.siteIds.includes(site.id)) region.siteIds.push(site.id);
    customerRegions.set(geography.regionKey, region);
    regionsByCustomer.set(customer.id, customerRegions);
    if (!country.regions.some((candidate) => candidate.key === region.key))
      country.regions.push(region);
    customerSites.set(site.id, siteRow);
    sitesByCustomer.set(customer.id, customerSites);
    if (!region.sites.some((candidate) => candidate.siteId === siteRow.siteId)) {
      region.sites.push(siteRow);
    }
  }

  for (const row of byCustomer.values()) {
    row.countries = [...(countriesByCustomer.get(row.customerId)?.values() ?? [])]
      .map((country) => ({
        ...country,
        regions: country.regions
          .map((region) => ({
            ...region,
            sites: [...region.sites].sort((left, right) => left.label.localeCompare(right.label)),
          }))
          .sort((left, right) => left.label.localeCompare(right.label)),
      }))
      .sort((left, right) => left.label.localeCompare(right.label));
  }

  const rows = [...byCustomer.values()].sort((left, right) =>
    left.customerName.localeCompare(right.customerName),
  );
  return { rows, totals, total };
}

export function computeCustomerAllocation(
  items: Item[],
  partsById: Map<string, Part>,
  sitesById: Map<string, Site>,
  customersById: Map<string, Customer>,
): CustomerAllocation {
  const entries: AllocationEntry<DeviceCounts>[] = [];
  for (const item of items) {
    const category = deviceCategoryForPart(partsById.get(item.partId));
    if (!category) continue;
    entries.push({ siteId: item.siteId, add: (counts) => increment(counts, category), items: 1 });
  }
  return buildCustomerAllocation(entries, sitesById, customersById, emptyCounts);
}

// Tidal's own customer holds the warehouse fleet and test rigs, not a
// customer's allocation, so the camera view leaves it out.
export const TIDAL_CUSTOMER_ID = '5f33142a-e543-4fd8-81e1-c2dbb1e52b15';

export const cameraAllocationColumns = [
  {
    key: 'allocated',
    label: 'Allocated',
    title: "Every camera assigned to the customer's sites, in any status and of any build.",
  },
  {
    key: 'activePens',
    label: 'Active pens',
    title:
      'Pens a camera reported from during the month, each counted once: a replacement in the same pen, or a camera that moved between pens, counts 1. Billed in full.',
  },
  {
    key: 'notReporting',
    label: 'Allocated, not reporting',
    title:
      'Cameras assigned to the customer, Active, Shipped or Delivered, that sent no data during the month. Billed only up to the contracted number.',
  },
  {
    key: 'contracted',
    label: 'Contracted',
    title: "Cameras in the customer's contract. Coming soon: contract numbers aren't recorded yet.",
  },
  {
    key: 'inService',
    label: 'In service',
    title: `Active today, and sent data in the last ${GRACE_DAYS} days.`,
  },
  {
    key: 'goneQuiet',
    label: 'Gone quiet',
    title: `Active today, but no data for over ${GRACE_DAYS} days.`,
  },
  {
    key: 'neverReported',
    label: 'Never reported',
    title: 'Active today, but has never sent data.',
  },
  {
    key: 'shipped',
    label: 'Shipped',
    title: 'Shipped to customer today: on its way or being installed.',
  },
  {
    key: 'delivered',
    label: 'Delivered',
    title: 'Delivered to customer, not yet Active.',
  },
  {
    key: 'replaced',
    label: 'Replaced',
    title: 'Swapped out for another camera at the same pen, awaiting return. Not billed.',
  },
  {
    key: 'experimental',
    label: 'Experimental',
    title:
      'Active on a dual-deployment pen (such as M4b) or a B-twin site. Also counted in the columns above.',
  },
] as const;

export type CameraAllocationColumn = (typeof cameraAllocationColumns)[number]['key'];
/** Contracted has no data yet, so it isn't counted. */
export type CameraCounts = Record<Exclude<CameraAllocationColumn, 'contracted'>, number>;

function emptyCameraCounts(): CameraCounts {
  return {
    allocated: 0,
    activePens: 0,
    notReporting: 0,
    inService: 0,
    goneQuiet: 0,
    neverReported: 0,
    shipped: 0,
    delivered: 0,
    replaced: 0,
    experimental: 0,
  };
}

/**
 * How many pens a set of cameras covers, each pen and each camera counted at
 * most once: the largest matching of cameras to the pens they reported from.
 * Two cameras in one pen (a replacement) count 1, and so does one camera
 * that moved between two pens.
 */
export function countActivePens(rows: readonly { deviceId: string; pen: string }[]): number {
  const pensByCamera = new Map<string, string[]>();
  for (const row of rows) {
    const pens = pensByCamera.get(row.deviceId) ?? [];
    if (!pens.includes(row.pen)) pens.push(row.pen);
    pensByCamera.set(row.deviceId, pens);
  }
  const cameraByPen = new Map<string, string>();
  const assign = (camera: string, seen: Set<string>): boolean => {
    for (const pen of pensByCamera.get(camera) ?? []) {
      if (seen.has(pen)) continue;
      seen.add(pen);
      const holder = cameraByPen.get(pen);
      if (holder === undefined || assign(holder, seen)) {
        cameraByPen.set(pen, camera);
        return true;
      }
    }
    return false;
  };
  let matched = 0;
  for (const camera of pensByCamera.keys()) if (assign(camera, new Set())) matched += 1;
  return matched;
}

/** Statuses of cameras that bill as allocated when they don't report. */
const allocatedStatuses = new Set(['ACTIVE', 'SHIPPED_TO_CUSTOMER', 'DELIVERED_TO_CUSTOMER']);

/**
 * Cameras per customer, country, region and site, all generations combined
 * (tidaltech/koi#8447). Active pens come from the month's heartbeats, at the
 * site each camera reported from; everything else counts cameras at their
 * assigned site, by today's status. A customer is billed
 * max(Active pens, min(Contracted, Active pens + Allocated, not reporting)).
 */
export function computeCustomerCameraAllocation(
  items: readonly Item[],
  partsById: ReadonlyMap<string, Part>,
  sitesById: ReadonlyMap<string, Site>,
  customersById: ReadonlyMap<string, Customer>,
  usages: Readonly<Record<string, ItemUsage>>,
  penActivity: readonly PenActivity[] = [],
  today: Date = new Date(),
): CustomerAllocation<CameraCounts> {
  // Every camera, whatever its build: a few Gen1 cameras in the field are
  // recorded against the EVT part, and billing shouldn't lose them.
  const cameras = items.filter((item) => partsById.get(item.partId)?.type === 'CAMERA_SYSTEM');
  const cameraIds = new Set(cameras.map((item) => item.id.toUpperCase()));
  const reported = new Set<string>();
  const activityBySite = new Map<string, PenActivity[]>();
  for (const row of penActivity) {
    const id = row.deviceId.toUpperCase();
    if (!cameraIds.has(id)) continue;
    reported.add(id);
    const rows = activityBySite.get(row.siteId) ?? [];
    rows.push({ ...row, deviceId: id });
    activityBySite.set(row.siteId, rows);
  }

  const entries: AllocationEntry<CameraCounts>[] = [];
  for (const item of cameras) {
    const usage = usages[item.id];
    const notReporting =
      item.status !== undefined &&
      allocatedStatuses.has(item.status) &&
      !reported.has(item.id.toUpperCase());
    entries.push({
      siteId: item.siteId,
      items: 1,
      add: (counts) => {
        counts.allocated += 1;
        if (notReporting) counts.notReporting += 1;
        switch (item.status) {
          case 'ACTIVE': {
            if (!usage?.lastUsed) counts.neverReported += 1;
            else if (daysSince(usage.lastUsed, today) > GRACE_DAYS) counts.goneQuiet += 1;
            else counts.inService += 1;
            const siteCode = sitesById.get(item.locationSiteId ?? item.siteId)?.name;
            if (isDualDeployment(usage, siteCode)) counts.experimental += 1;
            break;
          }
          case 'SHIPPED_TO_CUSTOMER':
            counts.shipped += 1;
            break;
          case 'DELIVERED_TO_CUSTOMER':
            counts.delivered += 1;
            break;
          case 'REPLACED':
            counts.replaced += 1;
            break;
          default:
            break;
        }
      },
    });
  }
  for (const [siteId, rows] of activityBySite) {
    const pens = countActivePens(rows);
    entries.push({ siteId, items: 0, add: (counts) => (counts.activePens += pens) });
  }
  return buildCustomerAllocation(
    entries,
    sitesById,
    customersById,
    emptyCameraCounts,
    (customer) => customer.id !== TIDAL_CUSTOMER_ID,
  );
}

export interface SystemStatusRow {
  status: ItemStatus;
  statusLabel: string;
  counts: DeviceCounts;
  total: number;
}

export interface SystemGroupRow {
  key: string;
  label: string;
  rows: SystemStatusRow[];
  totals: DeviceCounts;
  total: number;
}

export function computeSystemSummary(
  items: Item[],
  partsById: Map<string, Part>,
): { groups: SystemGroupRow[]; totals: DeviceCounts; total: number; omitted: number } {
  const statusCounts = new Map<ItemStatus, DeviceCounts>();
  let categorizedItems = 0;

  for (const item of items) {
    const category = deviceCategoryForPart(partsById.get(item.partId));
    if (!category) continue;
    categorizedItems += 1;
    const status = item.status ?? 'STATUS_UNSPECIFIED';
    const counts = statusCounts.get(status) ?? emptyCounts();
    increment(counts, category);
    statusCounts.set(status, counts);
  }

  const groups = systemGroups
    .map((group) => {
      const rows = group.statuses
        .map((status) => {
          const counts = statusCounts.get(status) ?? emptyCounts();
          return {
            status,
            statusLabel: statusLabels[status],
            counts,
            total: totalCounts(counts),
          };
        })
        .filter((row) => row.total > 0);
      const totals = emptyCounts();
      for (const row of rows) addCounts(totals, row.counts);
      return {
        key: group.key,
        label: group.label,
        rows,
        totals,
        total: totalCounts(totals),
      };
    })
    .filter((group) => group.total > 0);

  const totals = emptyCounts();
  for (const group of groups) addCounts(totals, group.totals);
  const total = totalCounts(totals);
  return { groups, totals, total, omitted: categorizedItems - total };
}

export interface RevisionBreakdownRow {
  status: ItemStatus;
  statusLabel: string;
  /** Count per entry of `revisions`, then the unversioned count last. */
  counts: number[];
  total: number;
}

export interface RevisionBreakdown {
  category: DeviceCategory;
  label: string;
  /** Recorded hardware revisions, in version order (e.g. G2.0, G2.1). */
  revisions: string[];
  rows: RevisionBreakdownRow[];
  /** Column totals, aligned with each row's `counts`. */
  totals: number[];
  total: number;
  /** Items in this category with no hardware revision recorded. */
  unversioned: number;
}

const versionOrder = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

/**
 * Status by recorded hardware revision (G2.1, G2.2, ...) for one device
 * category, so the versions in the fleet can be told apart
 * (tidaltech/koi#8153). Items without a revision get their own column
 * rather than being dropped. Returns null when no item in the category
 * has a revision recorded, since a single "No version" column says nothing.
 */
export function computeRevisionBreakdown(
  items: readonly Item[],
  partsById: ReadonlyMap<string, Part>,
  category: DeviceCategory,
): RevisionBreakdown | null {
  const label = deviceCategories.find((candidate) => candidate.key === category)?.label ?? category;
  const inCategory = items.filter(
    (item) => deviceCategoryForPart(partsById.get(item.partId)) === category,
  );
  const revisions = [
    ...new Set(
      inCategory.map((item) => item.hardwareRevision?.trim() ?? '').filter((value) => value),
    ),
  ].sort((left, right) => versionOrder.compare(left, right));
  if (revisions.length === 0) return null;

  const column = new Map(revisions.map((revision, index) => [revision, index]));
  const byStatus = new Map<ItemStatus, number[]>();
  for (const item of inCategory) {
    const status = item.status ?? 'STATUS_UNSPECIFIED';
    const counts = byStatus.get(status) ?? new Array<number>(revisions.length + 1).fill(0);
    const index = column.get(item.hardwareRevision?.trim() ?? '') ?? revisions.length;
    counts[index] = (counts[index] ?? 0) + 1;
    byStatus.set(status, counts);
  }

  const rows = [...byStatus.entries()]
    .map(([status, counts]) => ({
      status,
      statusLabel: statusLabels[status],
      counts,
      total: counts.reduce((sum, count) => sum + count, 0),
    }))
    .sort((a, b) => b.total - a.total || a.statusLabel.localeCompare(b.statusLabel));
  const totals = new Array<number>(revisions.length + 1).fill(0);
  for (const row of rows)
    row.counts.forEach((count, index) => (totals[index] = (totals[index] ?? 0) + count));
  return {
    category,
    label,
    revisions,
    rows,
    totals,
    total: inCategory.length,
    unversioned: totals[revisions.length] ?? 0,
  };
}
